import { FC, ReactNode, useState } from 'react';

import { clsx } from 'clsx';
import { AlertTriangle } from 'lucide-react';

import { OfferDraftFormValues } from './offerDraft';
import { OfferPhotoUploader } from './OfferPhotoUploader';
import { ProductContextSelectors } from './ProductContextSelectors';
import { ProductSelector } from './ProductSelector';
import { CardmarketProductLink } from '../../../../components/shared/CardmarketProductLink';
import { Alert, AlertTitle } from '../../../../components/ui/Alert';
import { Badge } from '../../../../components/ui/Badge';
import { Button } from '../../../../components/ui/Button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../../../../components/ui/Card';
import { Input } from '../../../../components/ui/Input';
import { Label } from '../../../../components/ui/Label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../../components/ui/Select';
import {
    CartonProduct,
    OfferPhoto,
    OfferPhotoUpload,
    OfferProperty,
    Universe,
} from '../../../../types';
import { DuplicateWarning } from '../../../../types';
import { SetData } from '../../../../types/shared';

interface Props {
    setData: SetData<OfferDraftFormValues>;
    offerProperties: OfferProperty[];
    selectedProduct: CartonProduct | null;
    setSelectedProduct: (
        product: CartonProduct,
    ) => Record<string, string> | void;
    productSearchSeed?: string | null;
    universes?: Universe[];
    selectedUniverseId?: number | null;
    onUniverseChange?: (universeId: number) => void;
    productTypes?: Universe['product_types'];
    selectedProductTypeId?: number | null;
    onProductTypeChange?: (productTypeId: number) => void;
    price: string;
    priceHelperText?: string | null;
    amount: string;
    propertySet: Record<string, string>;
    photos?: OfferPhotoUpload[];
    existingPhotos?: OfferPhoto[];
    removedPhotoIds?: number[];
    onRemoveExistingPhoto?: (photoId: number) => void;
    onPhotoUploadingChange?: (uploading: boolean) => void;
    initialProductOptions: CartonProduct[];
    errors: Partial<Record<keyof OfferDraftFormValues, string | string[]>>;
    isEditing?: boolean;
    embedded?: boolean;
    compact?: boolean;
    headerExtra?: ReactNode;
}

const firstErrorMessage = (error?: string | string[]) =>
    Array.isArray(error) ? error[0] : error;

const normalizeHint = (hint?: string | null) => {
    const value = hint?.trim();

    return value ? value : null;
};

export const FormFields: FC<Props> = ({
    setData,
    offerProperties,
    selectedProduct,
    setSelectedProduct,
    productSearchSeed,
    universes = [],
    selectedUniverseId,
    onUniverseChange,
    productTypes = [],
    selectedProductTypeId,
    onProductTypeChange,
    price,
    priceHelperText,
    amount,
    propertySet,
    photos,
    existingPhotos,
    removedPhotoIds,
    onRemoveExistingPhoto,
    onPhotoUploadingChange,
    initialProductOptions,
    errors,
    isEditing = false,
    embedded = false,
    compact = false,
    headerExtra,
}) => {
    const [duplicateWarning, setDuplicateWarning] =
        useState<DuplicateWarning | null>(null);
    const propertySetError = firstErrorMessage(errors.property_set);
    const productError = firstErrorMessage(errors.product_id);
    const priceError = firstErrorMessage(errors.price);
    const amountError = firstErrorMessage(errors.amount);
    const photosError = firstErrorMessage(errors.photos);
    const isCompact = embedded || compact;
    const showPropertyHints = !isEditing;

    const content = (
        <>
            {!isEditing && (
                <ProductContextSelectors
                    universes={universes}
                    selectedUniverseId={selectedUniverseId}
                    onUniverseChange={onUniverseChange}
                    productTypes={productTypes}
                    selectedProductTypeId={selectedProductTypeId}
                    onProductTypeChange={onProductTypeChange}
                />
            )}

            <div className="space-y-2">
                <ProductSelector
                    setData={setData}
                    setSelected={(product) => {
                        const nextPropertySet = setSelectedProduct(product);
                        setDuplicateWarning(null);
                        return nextPropertySet;
                    }}
                    initialOptions={initialProductOptions}
                    selectedProduct={selectedProduct}
                    searchSeed={productSearchSeed}
                    selectedUniverseId={selectedUniverseId}
                    selectedProductTypeId={selectedProductTypeId}
                    onDuplicateCheck={(data) => setDuplicateWarning(data)}
                    isEditing={isEditing}
                />
                {productError && (
                    <p className="text-sm text-red-600">{productError}</p>
                )}

                {selectedProduct &&
                    (isCompact ? (
                        <div
                            className="rounded-xl border p-3"
                            data-testid="selected-product-card"
                        >
                            <div className="flex items-stretch gap-3">
                                <div className="w-16 shrink-0 rounded-md bg-muted/30 p-1 sm:w-20">
                                    <img
                                        src={selectedProduct.image}
                                        alt={selectedProduct.image_alt}
                                        className="h-full w-full object-contain"
                                    />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                                        <p className="max-w-full truncate font-medium">
                                            {selectedProduct.name}
                                        </p>
                                        <Badge data-testid="selected-product-expansion-tag">
                                            {selectedProduct.expansion_tag}
                                        </Badge>
                                        <Badge
                                            variant="outline"
                                            data-testid="selected-product-language"
                                        >
                                            {selectedProduct.language}
                                        </Badge>
                                        <CardmarketProductLink
                                            href={
                                                selectedProduct.cardmarket_url
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-2 flex h-35 flex-col items-start gap-4 sm:gap-6 md:flex-row">
                            <div className="flex h-full gap-2 md:gap-4">
                                <div className="relative aspect-[3/4] h-full rounded-lg">
                                    <img
                                        src={selectedProduct.image}
                                        alt={selectedProduct.image_alt}
                                        className="h-full object-cover"
                                    />
                                </div>
                                <div>
                                    <div className="mb-2 flex flex-wrap items-center gap-2">
                                        <Badge>
                                            {selectedProduct.expansion_tag}
                                        </Badge>
                                        <Badge variant="outline">
                                            {selectedProduct.language}
                                        </Badge>
                                        <CardmarketProductLink
                                            href={
                                                selectedProduct.cardmarket_url
                                            }
                                        />
                                    </div>
                                    <h2 className="mb-2 text-base font-bold sm:text-xl">
                                        {selectedProduct.name}
                                    </h2>
                                </div>
                            </div>
                        </div>
                    ))}

                {duplicateWarning && selectedProduct && (
                    <Alert variant="info" className="mt-4">
                        <AlertTitle>
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
                                <div className="flex items-center">
                                    <AlertTriangle className="mr-2 inline h-4 w-4 shrink-0" />
                                    Увага! {duplicateWarning.message}
                                </div>
                                {duplicateWarning.edit_url && (
                                    <div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            asChild
                                        >
                                            <a href={duplicateWarning.edit_url}>
                                                Редагувати існуючу пропозицію
                                            </a>
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </AlertTitle>
                    </Alert>
                )}
            </div>

            <div className="space-y-4">
                <div
                    className={clsx('grid grid-cols-1 md:grid-cols-2', {
                        'gap-3': isCompact,
                        'gap-4 sm:gap-6': !isCompact,
                    })}
                    data-testid="offer-properties-grid"
                >
                    {offerProperties &&
                        offerProperties.map((property) => {
                            const propertyHint = normalizeHint(property.hint);
                            const selectedOptionHint =
                                property.kind === 'collection'
                                    ? normalizeHint(
                                          property.offer_property_options.find(
                                              (option) =>
                                                  option.slug ===
                                                  propertySet[property.slug],
                                          )?.hint,
                                      )
                                    : null;
                            const displayedHint =
                                selectedOptionHint ?? propertyHint;

                            return (
                                <div key={property.slug}>
                                    <Label
                                        htmlFor={property.slug}
                                        className={clsx('mb-2', {
                                            'text-sm': isCompact,
                                            'text-base': !isCompact,
                                        })}
                                    >
                                        {property.name}
                                        {property.required && (
                                            <span
                                                aria-hidden="true"
                                                className="text-foreground"
                                            >
                                                *
                                            </span>
                                        )}
                                    </Label>
                                    {property.kind === 'collection' ? (
                                        <Select
                                            value={propertySet[property.slug]}
                                            onValueChange={(value) =>
                                                setData('property_set', {
                                                    ...propertySet,
                                                    [property.slug]: value,
                                                })
                                            }
                                        >
                                            <SelectTrigger
                                                id={property.slug}
                                                className={clsx(
                                                    {
                                                        'mt-1.5 h-9 text-sm':
                                                            isCompact,
                                                        'mt-2 h-8 text-sm sm:h-9':
                                                            !isCompact,
                                                    },
                                                    {
                                                        'border-red-600':
                                                            propertySetError,
                                                    },
                                                )}
                                            >
                                                <SelectValue placeholder="Оберіть значення" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {property.offer_property_options.map(
                                                    (option) => (
                                                        <SelectItem
                                                            key={option.slug}
                                                            value={option.slug}
                                                        >
                                                            {option.name}
                                                        </SelectItem>
                                                    ),
                                                )}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <Input
                                            id={property.slug}
                                            type={
                                                property.kind === 'number'
                                                    ? 'number'
                                                    : 'text'
                                            }
                                            maxLength={
                                                property.kind === 'string'
                                                    ? 120
                                                    : undefined
                                            }
                                            value={
                                                propertySet[property.slug] ?? ''
                                            }
                                            onChange={(e) =>
                                                setData('property_set', {
                                                    ...propertySet,
                                                    [property.slug]:
                                                        e.target.value,
                                                })
                                            }
                                            className={clsx(
                                                {
                                                    'mt-1.5 h-9 text-sm':
                                                        isCompact,
                                                    'mt-2 h-8 text-sm sm:h-9':
                                                        !isCompact,
                                                },
                                                {
                                                    'border-red-600':
                                                        propertySetError,
                                                },
                                            )}
                                            placeholder={
                                                property.kind === 'number'
                                                    ? 'Введіть число'
                                                    : 'Введіть текст'
                                            }
                                        />
                                    )}
                                    {showPropertyHints && displayedHint && (
                                        <p className="mt-1 text-xs leading-snug text-muted-foreground">
                                            {displayedHint}
                                        </p>
                                    )}
                                    {propertySetError && (
                                        <p className="text-sm text-red-600">
                                            {propertySetError}
                                        </p>
                                    )}
                                </div>
                            );
                        })}
                </div>
                {selectedProduct && (
                    <div
                        className={clsx('grid grid-cols-1 sm:grid-cols-2', {
                            'gap-3': isCompact,
                            'gap-4 sm:gap-6': !isCompact,
                        })}
                        data-testid="offer-pricing-grid"
                    >
                        <div>
                            <Label
                                htmlFor="quantity"
                                className={clsx({
                                    'text-sm': isCompact,
                                    'text-base': !isCompact,
                                })}
                            >
                                Кількість
                            </Label>
                            <Input
                                id="quantity"
                                type="number"
                                min="1"
                                value={amount}
                                className={clsx(
                                    {
                                        'mt-1.5 h-9 text-sm': isCompact,
                                        'mt-2 h-8 text-sm sm:h-9': !isCompact,
                                    },
                                    {
                                        'border-red-600': amountError,
                                    },
                                )}
                                onChange={(e) => {
                                    setData('amount', e.target.value);
                                }}
                                placeholder="Введіть кількість"
                            />
                            {amountError && (
                                <p className="text-sm text-red-600">
                                    {amountError}
                                </p>
                            )}
                        </div>

                        <div>
                            <Label
                                htmlFor="price"
                                className={clsx({
                                    'text-sm': isCompact,
                                    'text-base': !isCompact,
                                })}
                            >
                                Ціна за одиницю(₴)
                            </Label>
                            <Input
                                id="price"
                                type="number"
                                step="0.01"
                                min="0"
                                value={price}
                                onChange={(e) => {
                                    setData('price', e.target.value);
                                }}
                                className={clsx(
                                    {
                                        'mt-1.5 h-9 text-sm': isCompact,
                                        'mt-2 h-8 text-sm sm:h-9': !isCompact,
                                    },
                                    {
                                        'border-red-600': priceError,
                                    },
                                )}
                                placeholder="Введіть ціну"
                            />
                            {priceError && (
                                <p className="text-sm text-red-600">
                                    {priceError}
                                </p>
                            )}
                            {!priceError && priceHelperText && (
                                <p className="mt-1 text-xs leading-snug text-muted-foreground">
                                    {priceHelperText}
                                </p>
                            )}
                        </div>
                    </div>
                )}
                {selectedProduct && photos !== undefined && (
                    <OfferPhotoUploader
                        photos={photos}
                        onChange={(nextPhotos) => setData('photos', nextPhotos)}
                        required={selectedProduct.offer_photos_required}
                        error={photosError}
                        existingPhotos={existingPhotos}
                        removedPhotoIds={removedPhotoIds}
                        onRemoveExistingPhoto={onRemoveExistingPhoto}
                        onUploadingChange={onPhotoUploadingChange}
                    />
                )}
            </div>
        </>
    );

    if (embedded) {
        return <div className="space-y-4">{content}</div>;
    }

    return (
        <div>
            <Card className="border-0 shadow-none sm:border-0 md:border md:shadow-sm">
                <CardHeader className="px-0 sm:px-0 md:px-6">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        {isEditing ? (
                            <CardTitle className="text-lg sm:text-xl">
                                Редагувати пропозицію
                            </CardTitle>
                        ) : (
                            <CardTitle className="text-base sm:text-lg">
                                Створити пропозицію
                            </CardTitle>
                        )}
                        {headerExtra && (
                            <div className="w-full sm:w-auto">
                                {headerExtra}
                            </div>
                        )}
                    </div>
                    {isEditing ? (
                        <CardDescription>
                            Оновіть деталі вашої пропозиції
                        </CardDescription>
                    ) : (
                        <CardDescription>
                            Виставте вашу карту на продаж на CartOn
                        </CardDescription>
                    )}
                </CardHeader>
                <CardContent
                    className={clsx('p-0 sm:p-0 md:p-6', {
                        'space-y-4': isCompact,
                        'space-y-6': !isCompact,
                    })}
                >
                    {content}
                </CardContent>
            </Card>
        </div>
    );
};
